import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/auth";
import { AppShell } from "@/components/AppShell";

import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Learn from "@/pages/Learn";
import Dashboard from "@/pages/Dashboard";
import Roster from "@/pages/Roster";
import Sessions from "@/pages/Sessions";
import Guides from "@/pages/Guides";
import RiderDetail from "@/pages/RiderDetail";
import Pathway from "@/pages/Pathway";
import RunView from "@/pages/RunView";
import Report from "@/pages/Report";
import Summary from "@/pages/Summary";
import ManageUsers from "@/pages/ManageUsers";
import Assignments from "@/pages/Assignments";
import Incidents from "@/pages/Incidents";
import Forms from "@/pages/Forms";
import Meetings from "@/pages/Meetings";
import Documents from "@/pages/Documents";
import Analytics from "@/pages/Analytics";

import RiderForm from "@/forms/RiderForm";
import TrainerForm from "@/forms/TrainerForm";
import RunForm from "@/forms/RunForm";
import TestForm from "@/forms/TestForm";
import IncidentForm from "@/forms/IncidentForm";
import FeedbackForm from "@/forms/FeedbackForm";
import LogActivity from "@/forms/LogActivity";
import IpsgaForm from "@/forms/IpsgaForm";
import BookTest from "@/forms/BookTest";
import BookRun from "@/forms/BookRun";
import TrainerCheckRide from "@/forms/TrainerCheckRide";
import AssessorCheckRide from "@/forms/AssessorCheckRide";

const queryClient = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false } } });

function Protected() {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <div className="min-h-screen" style={{ background: "#0F2350" }} />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/roster" element={<Roster />} />
        <Route path="/sessions" element={<Sessions />} />
        <Route path="/guides" element={<Guides />} />
        <Route path="/incidents" element={<Incidents />} />
        <Route path="/meetings" element={<Meetings />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/summary" element={<Summary />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/assignments" element={<Assignments />} />
        <Route path="/manage-users" element={<ManageUsers />} />
        <Route path="/forms" element={<Forms />} />
        <Route path="/rider/:id" element={<RiderDetail />} />
        <Route path="/pathway/:id" element={<Pathway />} />
        <Route path="/run/:id" element={<RunView />} />
        <Route path="/report/:id" element={<Report />} />
        <Route path="/rider-form" element={<RiderForm />} />
        <Route path="/trainer-form" element={<TrainerForm />} />
        <Route path="/run-form" element={<RunForm />} />
        <Route path="/test-form" element={<TestForm />} />
        <Route path="/incident-form" element={<IncidentForm />} />
        <Route path="/feedback-form" element={<FeedbackForm />} />
        <Route path="/log-activity" element={<LogActivity />} />
        <Route path="/ipsga-form" element={<IpsgaForm />} />
        <Route path="/book-test" element={<BookTest />} />
        <Route path="/book-run" element={<BookRun />} />
        <Route path="/trainer-checkride" element={<TrainerCheckRide />} />
        <Route path="/assessor-checkride" element={<AssessorCheckRide />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}

function Gate() {
  const { user, ready } = useAuth();
  if (ready && user) return <Navigate to="/" replace />;
  return <Login />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Gate />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/learn/tutor" element={<Learn role="tutor" />} />
            <Route path="/learn/rider" element={<Learn role="rider" />} />
            <Route path="/*" element={<Protected />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
